import { AdaptiveController, type ControllerProfile } from '@spectrabus/adaptive-controller';
import {
  allocateCarriers,
  estimateOfdmTransmission,
  type AudioCarrierPlan,
  type AudioModulation,
} from '@spectrabus/audio-modem';
import {
  acceptSenderHandshake,
  completeSenderHandshake,
  createDiscoveryCommitment,
  createKeyConfirmation,
  createSenderHandshake,
  derivePhysicalFrameAuthKey,
  IntegrityError,
  hashHex,
  openPhysicalFrameEnvelope,
  openSealedChunk,
  openSealedObject,
  sealPhysicalFrameEnvelope,
  sealObject,
  verifyKeyConfirmation,
  type SealedObject,
  type SplitPeerSession,
} from '@spectrabus/crypto';
import {
  GenerationDecoder,
  GenerationEncoder,
  type FountainSymbol,
} from '@spectrabus/fountain';
import {
  encodeOpticalPayload,
  getOpticalFrameGeometry,
  OPTICAL_HEADER_BYTES,
  type OpticalFrame,
} from '@spectrabus/optical-encoder';
import {
  DEFAULT_CHUNK_SIZE,
  ProtocolError,
  SequenceReplayWindow,
  assertPhaseTransition,
  bytesToHex,
  decodeTransportPacket,
  encodeTransportPacket,
  equalBytes,
  hexToBytes,
  randomIdentifier,
  type Direction,
  type ObjectDescriptor,
  type PhysicalChannel,
  type TransferPhase,
  type TransportPacket,
} from '@spectrabus/protocol';
import {
  metric,
  type CarrierTelemetry,
  type ChannelCounters,
  type GenerationTelemetry,
  type IntegrityCheck,
  type LinkSnapshot,
  type SpatialRegionTelemetry,
} from '@spectrabus/telemetry';

interface MutableGeneration {
  readonly encoder: GenerationEncoder;
  readonly decoder: GenerationDecoder;
  nextEquationId: number;
  opticalContributions: number;
  acousticContributions: number;
  verified: boolean;
  reconstructed: Uint8Array | null;
}

interface MutableCounters {
  accepted: number;
  rejected: number;
  duplicate: number;
  independent: number;
}

interface ConfirmationStep {
  readonly afterMs: number;
  readonly direction: Direction;
  readonly channel: PhysicalChannel;
  readonly sequence: number;
}

interface PendingChannelAttempt {
  readonly channel: PhysicalChannel;
  readonly generation: MutableGeneration;
  readonly packet: TransportPacket;
  readonly authenticatedWire: Uint8Array;
  readonly profileId: string;
  readonly timingProfileKey: string;
  readonly durationSeconds: number;
}

const ACTIVE_CHANNELS = ['optical', 'acoustic'] as const;
const SCHEDULER_QUANTUM_MS = 50;
const OPTICAL_REFRESH_HZ = 90;
const OPTICAL_CRC_BYTES = 4;
const MAX_EMISSIONS_PER_QUANTUM = 256;
const OPTICAL_PHYSICAL_PAYLOAD_BYTES =
  Math.floor(getOpticalFrameGeometry().dataCellIndices.length / 8) -
  OPTICAL_HEADER_BYTES -
  OPTICAL_CRC_BYTES;
const CONFIRMATION_STEPS: readonly ConfirmationStep[] = [
  { afterMs: 100, direction: 'sender-to-receiver', channel: 'optical', sequence: 1 },
  { afterMs: 200, direction: 'receiver-to-sender', channel: 'acoustic', sequence: 2 },
  { afterMs: 300, direction: 'sender-to-receiver', channel: 'acoustic', sequence: 3 },
  { afterMs: 400, direction: 'receiver-to-sender', channel: 'optical', sequence: 4 },
];

export interface DemoObjectInput {
  readonly bytes: Uint8Array;
  readonly name: string;
  readonly mimeType: string;
}

export interface DemoPreparation {
  readonly engine: DemoTransfer;
  readonly senderHash: string;
  readonly objectBytes: number;
}

export interface DemoTransportAirtimeEstimate {
  readonly payloadBytes: number;
  readonly opticalPayloadBytesPerRaster: number;
  readonly opticalRasterFrames: number;
  readonly opticalDurationSeconds: number;
  readonly acousticBitsPerSymbol: number;
  readonly acousticDurationSeconds: number;
  readonly aggregatePayloadBitsPerSecond: number;
}

function xorshift(state: number): number {
  let value = state >>> 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return value >>> 0;
}

function frozenCounters(counter: MutableCounters): ChannelCounters {
  return { ...counter };
}

const BASE64_URL_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function base64UrlEncode(bytes: Uint8Array): string {
  let encoded = '';
  for (let offset = 0; offset < bytes.length; offset += 3) {
    const first = bytes[offset] ?? 0;
    const second = bytes[offset + 1] ?? 0;
    const third = bytes[offset + 2] ?? 0;
    encoded += BASE64_URL_ALPHABET.charAt(first >>> 2);
    encoded += BASE64_URL_ALPHABET.charAt(((first & 0x03) << 4) | (second >>> 4));
    if (offset + 1 < bytes.length) {
      encoded += BASE64_URL_ALPHABET.charAt(((second & 0x0f) << 2) | (third >>> 6));
    }
    if (offset + 2 < bytes.length) {
      encoded += BASE64_URL_ALPHABET.charAt(third & 0x3f);
    }
  }
  return encoded;
}

function phaseDetail(phase: TransferPhase, profile: ControllerProfile): string {
  switch (phase) {
    case 'IDLE':
      return 'Select an object to create a sealed transfer session';
    case 'PREPARE':
      return 'Chunking, sealing and committing the object manifest';
    case 'DISCOVER':
      return 'Publishing bootstrap metadata over robust visual preamble';
    case 'CALIBRATE':
      return 'Sounding optical regions and audible multicarrier response';
    case 'KEY_CONFIRM':
      return 'Binding the ephemeral session context to both channels';
    case 'TRANSFER':
      return profile.reason;
    case 'REPAIR':
      return 'Fresh equations target the remaining rank deficits';
    case 'VERIFY':
      return 'Transport is withheld while the integrity chain resolves';
    case 'COMPLETE':
      return 'Deterministic receiver verified a byte-identical object';
    case 'FAILED':
      return 'Object withheld after an integrity or protocol failure';
  }
}

export function createReferenceObject(size = 12 * 1024 * 1024): DemoObjectInput {
  if (size < 1 || size > 25 * 1024 * 1024) {
    throw new ProtocolError('REFERENCE_SIZE', 'Reference object must be between one byte and 25 MiB.');
  }
  const bytes = new Uint8Array(size);
  const heading = new TextEncoder().encode(
    'SPECTRABUS REFERENCE OBJECT\nDeterministic payload; physical channel simulated.\n',
  );
  bytes.set(heading.subarray(0, Math.min(heading.length, bytes.length)));
  let state = 0x7a31e09c;
  for (let index = heading.length; index < bytes.length; index += 1) {
    state = xorshift(state);
    bytes[index] = (state ^ index ^ (index >>> 8)) & 0xff;
  }
  const megabytes = size / (1024 * 1024);
  const sizeLabel = megabytes >= 1 ? `${Math.round(megabytes)}m` : `${Math.round(size / 1024)}k`;
  return {
    bytes,
    name: `spectrabus-reference-${sizeLabel}.bin`,
    mimeType: 'application/octet-stream',
  };
}

export class DemoTransfer {
  readonly #sealed: SealedObject;
  readonly #seed: string;
  readonly #sessionId: string;
  readonly #sessionTag: Uint8Array;
  readonly #sessionFingerprint: string;
  readonly #sessionPublicKey: string;
  readonly #senderSession: SplitPeerSession;
  readonly #receiverSession: SplitPeerSession;
  readonly #discoveryCommitment: string;
  readonly #encodedOffer: string;
  readonly #offerWireBytes: number;
  readonly #responseWireBytes: number;
  readonly #activeChannels: readonly PhysicalChannel[];
  readonly #senderToReceiverConfirmed = new Set<PhysicalChannel>();
  readonly #receiverToSenderConfirmed = new Set<PhysicalChannel>();
  readonly #confirmationReplayWindows = new Map<string, SequenceReplayWindow>();
  readonly #dataReplayWindows: Record<PhysicalChannel, SequenceReplayWindow> = {
    optical: new SequenceReplayWindow(),
    acoustic: new SequenceReplayWindow(),
  };
  readonly #frameAuthKey: Uint8Array;
  readonly #objectTag: Uint8Array;
  readonly #generations: MutableGeneration[];
  readonly #reconstructedChunks: Array<Uint8Array | null>;
  readonly #controller = new AdaptiveController();
  readonly #counters: Record<PhysicalChannel, MutableCounters> = {
    optical: { accepted: 0, rejected: 0, duplicate: 0, independent: 0 },
    acoustic: { accepted: 0, rejected: 0, duplicate: 0, independent: 0 },
  };
  readonly #verifiedByChannel: Record<PhysicalChannel, number> = {
    optical: 0,
    acoustic: 0,
  };
  readonly #emittedWireBytes: Record<PhysicalChannel, number> = {
    optical: 0,
    acoustic: 0,
  };
  readonly #channelTimeCreditSeconds: Record<PhysicalChannel, number> = {
    optical: 0,
    acoustic: 0,
  };
  readonly #pendingAttempts: Record<PhysicalChannel, PendingChannelAttempt | null> = {
    optical: null,
    acoustic: null,
  };

  #phase: TransferPhase = 'DISCOVER';
  #phaseElapsedMs = 0;
  #elapsedMs = 0;
  #transferElapsedMs = 0;
  #sequence = 0;
  #generationCursor = 0;
  #randomState = 0x41d4c3e6;
  #profile: ControllerProfile;
  #currentFrame: OpticalFrame;
  #verifiedBytes = 0;
  #integrityViolations = 0;
  #failure: string | null = null;
  #downloadBytes: Uint8Array | null = null;
  #verificationStep = 0;
  #verificationResolved: Uint8Array | null = null;
  #receiverObjectHash: string | null = null;
  #confirmationWireBytes = 0;
  #controllerObservedVerifiedBytes = 0;
  #controllerObservedAttempts = 0;
  #controllerObservedIndependent = 0;
  #controllerObservedDuplicates = 0;
  #schedulerAccumulatorMs = 0;

  private constructor(
    sealed: SealedObject,
    senderSession: SplitPeerSession,
    receiverSession: SplitPeerSession,
    offerWire: Uint8Array,
    responseWireBytes: number,
    frameAuthKey: Uint8Array,
  ) {
    this.#sealed = sealed;
    this.#senderSession = senderSession;
    this.#receiverSession = receiverSession;
    // Cryptographic identifiers remain fresh for every preparation. Fountain
    // coefficients instead derive from the committed object so the named demo
    // scenario is reproducible across runs and tick partitions.
    this.#seed = `spectrabus-demo-scenario-v1:${sealed.manifest.objectHash}`;
    this.#sessionId = senderSession.sessionId;
    this.#sessionTag = hexToBytes(senderSession.sessionId).slice(0, 8);
    this.#sessionFingerprint = senderSession.keyFingerprint;
    this.#sessionPublicKey = bytesToHex(senderSession.senderPublicKey);
    this.#discoveryCommitment = senderSession.discoveryCommitment;
    this.#offerWireBytes = offerWire.length;
    this.#encodedOffer = base64UrlEncode(offerWire);
    this.#responseWireBytes = responseWireBytes;
    this.#activeChannels = Object.freeze([...senderSession.channels]);
    this.#frameAuthKey = frameAuthKey.slice();
    this.#objectTag = hexToBytes(sealed.objectId).slice(0, 8);
    this.#generations = sealed.chunks.map((chunk) => {
      const encoder = new GenerationEncoder(this.#seed, chunk.index, chunk.ciphertext);
      return {
        encoder,
        decoder: new GenerationDecoder(
          this.#seed,
          chunk.index,
          encoder.sourceCount,
          encoder.symbolSize,
          encoder.validBytes,
        ),
        nextEquationId: 0,
        opticalContributions: 0,
        acousticContributions: 0,
        verified: false,
        reconstructed: null,
      };
    });
    this.#reconstructedChunks = Array.from({ length: sealed.chunks.length }, () => null);
    this.#profile = this.#controller.fallback();
    if (this.#generations.length === 0) {
      throw new ProtocolError('DEMO_EMPTY', 'Prepared object did not produce a transport generation.');
    }
    // Until mutual key confirmation authorises TRANSFER, the live raster must
    // contain only the canonical public offer plus the O1 finder/timing pilots.
    this.#currentFrame = encodeOpticalPayload(offerWire, 0);
  }

  public static async prepare(input: DemoObjectInput): Promise<DemoPreparation> {
    const descriptor: Omit<ObjectDescriptor, 'byteLength'> = {
      name: sanitiseFilename(input.name),
      mimeType: sanitiseMimeType(input.mimeType),
    };
    const sessionId = randomIdentifier(16);
    const transferId = randomIdentifier(16);
    const senderHash = await hashHex(input.bytes);
    const discoveryCommitment = await createDiscoveryCommitment({
      ...descriptor,
      byteLength: input.bytes.length,
      objectHash: senderHash,
      chunkSize: DEFAULT_CHUNK_SIZE,
      compression: 'none',
    });
    const senderHandshake = await createSenderHandshake({
      sessionId,
      transferId,
      discoveryCommitment,
      offerSequence: 0,
      channels: ACTIVE_CHANNELS,
    });
    const receiverHandshake = await acceptSenderHandshake(senderHandshake.offerBytes, {
      responseSequence: 0,
      expectation: {
        sessionId,
        transferId,
        discoveryCommitment,
        offerSequence: 0,
        channels: ACTIVE_CHANNELS,
      },
    });
    const senderSession = await completeSenderHandshake(
      senderHandshake,
      receiverHandshake.responseBytes,
    );
    const receiverSession = receiverHandshake.session;
    if (
      !equalBytes(senderSession.objectRootKey, receiverSession.objectRootKey) ||
      senderSession.transcriptHash !== receiverSession.transcriptHash ||
      senderSession.keyFingerprint !== receiverSession.keyFingerprint
    ) {
      throw new ProtocolError(
        'DEMO_HANDSHAKE_MISMATCH',
        'Split peers derived different transcript-bound session material.',
      );
    }
    const sealed = await sealObject(
      input.bytes,
      descriptor,
      DEFAULT_CHUNK_SIZE,
      senderSession.objectRootKey,
    );
    const sealedDiscoveryCommitment = await createDiscoveryCommitment({
      name: sealed.manifest.name,
      mimeType: sealed.manifest.mimeType,
      byteLength: sealed.manifest.byteLength,
      objectHash: sealed.manifest.objectHash,
      chunkSize: sealed.manifest.chunkSize,
      compression: sealed.manifest.compression,
    });
    if (
      sealed.manifest.objectHash !== senderHash ||
      sealedDiscoveryCommitment !== discoveryCommitment
    ) {
      throw new ProtocolError(
        'DEMO_DISCOVERY_COMMITMENT',
        'Sealed object does not match the discovery object commitment.',
      );
    }
    const frameAuthKey = await derivePhysicalFrameAuthKey(
      senderSession.objectRootKey,
      sessionId,
    );
    const engine = new DemoTransfer(
      sealed,
      senderSession,
      receiverSession,
      senderHandshake.offerBytes,
      receiverHandshake.responseBytes.length,
      frameAuthKey,
    );
    return {
      engine,
      senderHash,
      objectBytes: input.bytes.length,
    };
  }

  public get sessionId(): string {
    return this.#sessionId;
  }

  public async advance(deltaMs: number): Promise<LinkSnapshot> {
    if (this.#isTerminal()) {
      return this.snapshot();
    }
    const boundedDelta = Math.max(1, Math.min(250, deltaMs));
    this.#elapsedMs += boundedDelta;

    try {
      let remainingMs = boundedDelta;
      while (remainingMs > 0 && !this.#isTerminal()) {
        if (this.#phase === 'DISCOVER') {
          const consumed = Math.min(remainingMs, 850 - this.#phaseElapsedMs);
          this.#phaseElapsedMs += consumed;
          remainingMs -= consumed;
          if (this.#phaseElapsedMs >= 850) this.#transition('CALIBRATE');
        } else if (this.#phase === 'CALIBRATE') {
          const consumed = Math.min(remainingMs, 1_650 - this.#phaseElapsedMs);
          this.#phaseElapsedMs += consumed;
          remainingMs -= consumed;
          if (this.#phaseElapsedMs >= 1_650) this.#transition('KEY_CONFIRM');
        } else if (this.#phase === 'KEY_CONFIRM') {
          const consumed = Math.min(remainingMs, Math.max(0, 650 - this.#phaseElapsedMs));
          this.#phaseElapsedMs += consumed;
          remainingMs -= consumed;
          await this.#advanceKeyConfirmation();
          if (this.#phaseElapsedMs >= 650) {
            // Every confirmation step is scheduled inside the 650 ms window,
            // so an unauthorised transfer at this point is an invariant
            // breach. Failing loudly here prevents advance() from spinning
            // with zero forward progress if a future change ever stalls
            // authorisation without throwing.
            if (!this.#transferAuthorised()) {
              throw new ProtocolError(
                'DEMO_KEY_CONFIRM_STALLED',
                'Key confirmation window elapsed without mutual channel authorisation.',
              );
            }
            this.#transition('TRANSFER');
          }
        } else if (this.#phase === 'TRANSFER' || this.#phase === 'REPAIR') {
          const untilQuantum =
            SCHEDULER_QUANTUM_MS - this.#schedulerAccumulatorMs;
          const consumed = Math.min(remainingMs, untilQuantum);
          this.#phaseElapsedMs += consumed;
          this.#transferElapsedMs += consumed;
          this.#schedulerAccumulatorMs += consumed;
          remainingMs -= consumed;
          if (this.#schedulerAccumulatorMs >= SCHEDULER_QUANTUM_MS) {
            this.#schedulerAccumulatorMs -= SCHEDULER_QUANTUM_MS;
            await this.#transferQuantum(SCHEDULER_QUANTUM_MS);
          }
        } else if (this.#phase === 'VERIFY') {
          const nextStepAt = (this.#verificationStep + 1) * 240;
          const consumed = Math.min(
            remainingMs,
            Math.max(0, nextStepAt - this.#phaseElapsedMs),
          );
          this.#phaseElapsedMs += consumed;
          remainingMs -= consumed;
          await this.#verify();
        } else {
          this.#phaseElapsedMs += remainingMs;
          remainingMs = 0;
        }
      }
    } catch (error) {
      this.#integrityViolations += error instanceof IntegrityError ? 1 : 0;
      this.#failure = error instanceof Error ? error.message : 'Unknown transfer failure.';
      this.#transition('FAILED');
    }
    return this.snapshot();
  }

  public snapshot(): LinkSnapshot {
    const now = this.#elapsedMs;
    const totalRank = this.#generations.reduce(
      (sum, generation) => sum + generation.decoder.rank,
      0,
    );
    const requiredRank = this.#generations.reduce(
      (sum, generation) => sum + generation.encoder.sourceCount,
      0,
    );
    const attempts =
      this.#counters.optical.accepted +
      this.#counters.optical.rejected +
      this.#counters.acoustic.accepted +
      this.#counters.acoustic.rejected;
    const duplicates = this.#counters.optical.duplicate + this.#counters.acoustic.duplicate;
    const transferSeconds = Math.max(0.001, this.#transferElapsedMs / 1_000);
    const opticalRate = (this.#verifiedByChannel.optical * 8) / transferSeconds;
    const acousticRate = (this.#verifiedByChannel.acoustic * 8) / transferSeconds;
    const opticalRawRate = (this.#emittedWireBytes.optical * 8) / transferSeconds;
    const acousticRawRate = (this.#emittedWireBytes.acoustic * 8) / transferSeconds;
    const independent = Math.max(
      1,
      this.#counters.optical.independent + this.#counters.acoustic.independent,
    );
    const progress = requiredRank === 0 ? 0 : totalRank / requiredRank;
    const incidents = incidentState(progress);
    const audioInterference = incidents[1]?.active ?? false;
    const displayObstruction = incidents[0]?.active ?? false;
    const carriers = createCarriers(audioInterference, this.#profile.acousticCeiling);
    const spatialRegions = createSpatialRegions(displayObstruction);

    return {
      provenance: {
        channel: 'simulated',
        raster: 'derived',
        carrierPlan: 'simulated',
        spatialQuality: 'simulated',
        scheduler: 'derived',
        admissions: 'simulated',
        handshake: 'derived',
        cryptography: 'derived',
        hashes: 'derived',
      },
      phase: this.#phase,
      phaseDetail:
        this.#phase === 'KEY_CONFIRM'
          ? `${this.#verifiedConfirmationCount()}/${this.#requiredConfirmationCount()} transcript-bound channel confirmations verified`
          : phaseDetail(this.#phase, this.#profile),
      handshake: {
        protocol: 'SBHS/1',
        keyAgreement: 'P-256 / HKDF-SHA-256',
        transcriptHash: this.#senderSession.transcriptHash,
        transferId: this.#senderSession.transferId,
        discoveryCommitment: this.#discoveryCommitment,
        encodedOffer: this.#encodedOffer,
        offerBytes: this.#offerWireBytes,
        responseBytes: this.#responseWireBytes,
        offerResponseBound:
          this.#senderSession.transcriptHash === this.#receiverSession.transcriptHash,
        confirmationBytes: this.#confirmationWireBytes,
        verifiedConfirmations: this.#verifiedConfirmationCount(),
        requiredConfirmations: this.#requiredConfirmationCount(),
        mutuallyConfirmedChannels: this.#mutuallyConfirmedChannels(),
        transferAuthorised: this.#transferAuthorised(),
        source: 'derived',
        transportSource: 'simulated',
      },
      sessionId: this.sessionId,
      sessionFingerprint: this.#sessionFingerprint,
      sessionPublicKey: this.#sessionPublicKey,
      manifestCommitment: this.#sealed.manifestCommitment,
      objectName: this.#sealed.descriptor.name,
      objectMimeType: this.#sealed.descriptor.mimeType,
      objectBytes: this.#sealed.descriptor.byteLength,
      elapsedMs: this.#elapsedMs,
      reconstructedFraction: progress,
      verifiedBytes: this.#verifiedBytes,
      totalIndependentSymbols: totalRank,
      requiredIndependentSymbols: requiredRank,
      unresolvedSymbols: Math.max(0, requiredRank - totalRank),
      integrityViolations: this.#integrityViolations,
      opticalProfile: this.#profile.opticalProfile,
      acousticProfile: `A1-OFDM / ${this.#profile.acousticCeiling}`,
      opticalGoodput: metric(opticalRate, 'bit/s', 'simulated', now, this.#counters.optical.accepted),
      acousticGoodput: metric(
        acousticRate,
        'bit/s',
        'simulated',
        now,
        this.#counters.acoustic.accepted,
      ),
      aggregateGoodput: metric(
        opticalRate + acousticRate,
        'bit/s',
        'simulated',
        now,
        this.#counters.optical.accepted + this.#counters.acoustic.accepted,
      ),
      opticalRawBitrate: metric(
        opticalRawRate,
        'bit/s',
        'simulated',
        now,
        this.#counters.optical.accepted + this.#counters.optical.rejected,
      ),
      acousticRawBitrate: metric(
        acousticRawRate,
        'bit/s',
        'simulated',
        now,
        this.#counters.acoustic.accepted + this.#counters.acoustic.rejected,
      ),
      cameraFps: metric(90, 'fps', 'simulated', now, 32),
      displayCadence: metric(
        OPTICAL_REFRESH_HZ / this.#profile.opticalDwellFrames,
        'frame/s',
        'simulated',
        now,
        32,
      ),
      feedbackRtt: metric(audioInterference ? 142 : 86, 'ms', 'simulated', now, 12),
      decoderLatency: metric(displayObstruction ? 118 : 42, 'ms', 'simulated', now, 32),
      fountainOverhead: metric(
        Math.max(0, (attempts - totalRank) / independent),
        'ratio',
        'simulated',
        now,
        attempts,
      ),
      duplicateRate: metric(
        attempts === 0 ? 0 : duplicates / attempts,
        'ratio',
        'simulated',
        now,
        attempts,
      ),
      spatialUtilisation: metric(
        spatialRegions.filter((region) => region.enabled).length / spatialRegions.length,
        'ratio',
        'simulated',
        now,
        16,
      ),
      environmentalStability: metric(
        displayObstruction ? 0.58 : audioInterference ? 0.78 : 0.96,
        'ratio',
        'simulated',
        now,
        32,
      ),
      opticalAllocation: this.#profile.opticalAllocation,
      acousticAllocation: this.#profile.acousticAllocation,
      generations: this.#generationTelemetry(),
      carriers,
      spatialRegions,
      integrityChecks: this.#integrityChecks(),
      incidents,
      counters: {
        optical: frozenCounters(this.#counters.optical),
        acoustic: frozenCounters(this.#counters.acoustic),
      },
      currentFrameCells: this.#currentFrame.cells.slice(),
      currentFrameWidth: this.#currentFrame.width,
      currentFrameHeight: this.#currentFrame.height,
      objectHash: this.#phase === 'COMPLETE' ? this.#receiverObjectHash : null,
      senderHash: this.#sealed.manifest.objectHash,
      downloadBytes: this.#downloadBytes?.slice() ?? null,
      failure: this.#failure,
    };
  }

  async #advanceKeyConfirmation(): Promise<void> {
    for (const step of CONFIRMATION_STEPS) {
      if (
        this.#phaseElapsedMs >= step.afterMs &&
        this.#activeChannels.includes(step.channel) &&
        !this.#confirmationSet(step.direction).has(step.channel)
      ) {
        await this.#performKeyConfirmation(step);
      }
    }
  }

  async #performKeyConfirmation(step: ConfirmationStep): Promise<void> {
    const origin =
      step.direction === 'sender-to-receiver'
        ? this.#senderSession
        : this.#receiverSession;
    const destination =
      step.direction === 'sender-to-receiver'
        ? this.#receiverSession
        : this.#senderSession;
    const wire = await createKeyConfirmation(
      origin,
      step.direction,
      step.channel,
      step.sequence,
    );
    const replayWindow = new SequenceReplayWindow();
    await verifyKeyConfirmation(destination, wire, {
      direction: step.direction,
      channel: step.channel,
      sequence: step.sequence,
      replayWindow,
    });
    this.#confirmationReplayWindows.set(
      `${step.direction}:${step.channel}`,
      replayWindow,
    );
    this.#confirmationSet(step.direction).add(step.channel);
    this.#confirmationWireBytes += wire.length;
  }

  #confirmationSet(direction: Direction): Set<PhysicalChannel> {
    return direction === 'sender-to-receiver'
      ? this.#senderToReceiverConfirmed
      : this.#receiverToSenderConfirmed;
  }

  #verifiedConfirmationCount(): number {
    return this.#senderToReceiverConfirmed.size + this.#receiverToSenderConfirmed.size;
  }

  #requiredConfirmationCount(): number {
    return this.#activeChannels.length * 2;
  }

  #mutuallyConfirmedChannels(): readonly PhysicalChannel[] {
    return this.#activeChannels.filter(
      (channel) =>
        this.#senderToReceiverConfirmed.has(channel) &&
        this.#receiverToSenderConfirmed.has(channel),
    );
  }

  #transferAuthorised(): boolean {
    return (
      this.#senderSession.transcriptHash === this.#receiverSession.transcriptHash &&
      this.#verifiedConfirmationCount() === this.#requiredConfirmationCount() &&
      this.#confirmationReplayWindows.size === this.#requiredConfirmationCount() &&
      this.#mutuallyConfirmedChannels().length === this.#activeChannels.length
    );
  }

  async #transferQuantum(windowElapsedMs: number): Promise<void> {
    const requiredRank = this.#generations.reduce(
      (sum, generation) => sum + generation.encoder.sourceCount,
      0,
    );
    const totalRank = this.#generations.reduce(
      (sum, generation) => sum + generation.decoder.rank,
      0,
    );
    const progress = totalRank / Math.max(1, requiredRank);
    const incidents = incidentState(progress);
    const displayObstruction = incidents[0]?.active ?? false;
    const audioInterference = incidents[1]?.active ?? false;
    const attempts =
      this.#counters.optical.accepted +
      this.#counters.optical.rejected +
      this.#counters.acoustic.accepted +
      this.#counters.acoustic.rejected;
    const independent =
      this.#counters.optical.independent + this.#counters.acoustic.independent;
    const duplicates = this.#counters.optical.duplicate + this.#counters.acoustic.duplicate;
    const windowAttempts = attempts - this.#controllerObservedAttempts;
    const windowIndependent = independent - this.#controllerObservedIndependent;
    const windowDuplicates = duplicates - this.#controllerObservedDuplicates;
    const windowVerifiedBytes = this.#verifiedBytes - this.#controllerObservedVerifiedBytes;
    this.#controllerObservedAttempts = attempts;
    this.#controllerObservedIndependent = independent;
    this.#controllerObservedDuplicates = duplicates;
    this.#controllerObservedVerifiedBytes = this.#verifiedBytes;
    const carrierPlan = createSimulatedAcousticPlan(
      audioInterference,
      this.#profile.acousticCeiling,
    );
    const unusableCarrierFraction =
      carrierPlan.carriers.filter((carrier) => !carrier.enabled).length /
      Math.max(1, carrierPlan.carriers.length);
    this.#profile = this.#controller.observe({
      opticalDecodeRate: displayObstruction ? 0.52 : 0.975,
      acousticPacketRate: audioInterference ? 0.31 : 0.91,
      duplicateRate: windowAttempts === 0 ? 0 : windowDuplicates / windowAttempts,
      decoderLatencyMs: displayObstruction ? 118 : 42,
      movement: displayObstruction ? 0.56 : 0.08,
      throttlingSuspected: false,
      unresolvedFraction: 1 - progress,
      verifiedNewInformationBits: windowVerifiedBytes * 8,
      elapsedMs: windowElapsedMs,
      fecOverhead:
        windowAttempts === 0
          ? 0
          : Math.max(0, (windowAttempts - windowIndependent) / Math.max(1, windowIndependent)),
      unusableCarrierFraction,
    });

    if (this.#phase === 'TRANSFER' && progress >= 0.88) {
      this.#transition('REPAIR');
    }

    const windowSeconds = windowElapsedMs / 1_000;
    this.#channelTimeCreditSeconds.optical +=
      windowSeconds * this.#profile.opticalAllocation;
    this.#channelTimeCreditSeconds.acoustic +=
      windowSeconds * this.#profile.acousticAllocation;

    for (const channel of ACTIVE_CHANNELS) {
      let emissions = 0;
      while (emissions < MAX_EMISSIONS_PER_QUANTUM) {
        let pending = this.#pendingAttempts[channel];
        if (pending?.generation.decoder.complete) {
          this.#pendingAttempts[channel] = null;
          pending = null;
        }
        if (
          pending !== null &&
          pending.timingProfileKey !==
            this.#timingProfileKey(channel, audioInterference)
        ) {
          pending = await this.#preparePendingAttempt(
            channel,
            pending.generation,
            pending.packet,
            audioInterference,
          );
          this.#pendingAttempts[channel] = pending;
        }
        if (pending === null) {
          pending = await this.#createPendingAttempt(channel, audioInterference);
          this.#pendingAttempts[channel] = pending;
        }
        if (
          pending === null ||
          this.#channelTimeCreditSeconds[channel] + Number.EPSILON <
            pending.durationSeconds
        ) {
          break;
        }
        this.#channelTimeCreditSeconds[channel] = Math.max(
          0,
          this.#channelTimeCreditSeconds[channel] - pending.durationSeconds,
        );
        this.#pendingAttempts[channel] = null;
        await this.#emitAttempt(pending, displayObstruction, audioInterference);
        emissions += 1;
      }
      if (emissions >= MAX_EMISSIONS_PER_QUANTUM) {
        throw new ProtocolError(
          'DEMO_SCHEDULER_BOUNDS',
          'Transport scheduler exceeded its bounded per-quantum emission count.',
        );
      }
    }

    if (this.#generations.every((generation) => generation.verified)) {
      this.#transition('VERIFY');
    }
  }

  async #createPendingAttempt(
    channel: PhysicalChannel,
    audioInterference: boolean,
  ): Promise<PendingChannelAttempt | null> {
    const generation = this.#nextIncompleteGeneration();
    if (generation === null) return null;
    const symbol = generation.encoder.createSymbol(generation.nextEquationId);
    generation.nextEquationId += 1;
    const packet = this.#packetFromSymbol(symbol, channel);
    return this.#preparePendingAttempt(
      channel,
      generation,
      packet,
      audioInterference,
    );
  }

  async #preparePendingAttempt(
    channel: PhysicalChannel,
    generation: MutableGeneration,
    packet: TransportPacket,
    audioInterference: boolean,
  ): Promise<PendingChannelAttempt> {
    const profileId = this.#profileForChannel(channel);
    const wire = encodeTransportPacket(packet);
    const authenticatedWire = await sealPhysicalFrameEnvelope(wire, this.#frameAuthKey, {
      sessionId: this.#sessionId,
      direction: packet.direction,
      channel,
      sequence: packet.sequence,
      symbolId: packet.equationId,
      profileId,
      contextId: this.#sealed.objectId,
    });
    const durationSeconds =
      channel === 'optical'
        ? estimateOpticalAirtime(authenticatedWire.length, this.#profile).durationSeconds
        : estimateOfdmTransmission(
            authenticatedWire.length,
            createSimulatedAcousticPlan(
              audioInterference,
              this.#profile.acousticCeiling,
            ),
          ).durationSeconds;
    return {
      channel,
      generation,
      packet,
      authenticatedWire,
      profileId,
      timingProfileKey: this.#timingProfileKey(channel, audioInterference),
      durationSeconds,
    };
  }

  #timingProfileKey(
    channel: PhysicalChannel,
    audioInterference: boolean,
  ): string {
    return channel === 'optical'
      ? `${this.#profile.opticalProfile}:${this.#profile.opticalCellScale}:${this.#profile.opticalDwellFrames}`
      : `A1-OFDM-${this.#profile.acousticCeiling}:${audioInterference ? 'notched' : 'clear'}`;
  }

  async #emitAttempt(
    pending: PendingChannelAttempt,
    displayObstruction: boolean,
    audioInterference: boolean,
  ): Promise<void> {
    const { authenticatedWire, channel, generation, packet, profileId } = pending;
    this.#emittedWireBytes[channel] += authenticatedWire.length;
    if (channel === 'optical') {
      this.#currentFrame = encodeOpticalPayload(authenticatedWire, packet.sequence);
    }
    const shouldFail = this.#channelFailure(
      channel,
      displayObstruction,
      audioInterference,
    );
    let decoded: TransportPacket;
    try {
      const received = shouldFail
        ? corruptWire(authenticatedWire, this.#randomState)
        : authenticatedWire;
      const opened = await openPhysicalFrameEnvelope(received, this.#frameAuthKey, {
        sessionId: this.#sessionId,
        direction: 'sender-to-receiver',
        channel,
        sequence: packet.sequence,
        symbolId: packet.equationId,
        profileId,
        contextId: this.#sealed.objectId,
        replayWindow: this.#dataReplayWindows[channel],
      });
      decoded = decodeTransportPacket(opened.payload);
      if (
        !equalBytes(decoded.sessionTag, this.#sessionTag) ||
        !equalBytes(decoded.objectTag, this.#objectTag) ||
        decoded.direction !== 'sender-to-receiver' ||
        decoded.channel !== channel ||
        decoded.generationId !== generation.encoder.generationId
      ) {
        throw new ProtocolError('SESSION_BINDING', 'Physical packet belongs to another session.');
      }
    } catch {
      this.#counters[channel].rejected += 1;
      return;
    }

    this.#counters[channel].accepted += 1;
    const admission = generation.decoder.add(this.#symbolFromPacket(decoded));
    if (admission.duplicate) {
      this.#counters[channel].duplicate += 1;
    }
    if (admission.independent) {
      this.#counters[channel].independent += 1;
      if (channel === 'optical') generation.opticalContributions += 1;
      else generation.acousticContributions += 1;
    }
    if (admission.reconstructed !== null && generation.reconstructed === null) {
      generation.reconstructed = admission.reconstructed;
      await this.#verifyGeneration(generation);
    }

    if (this.#randomUnit() < 0.022) {
      const duplicate = generation.decoder.add(this.#symbolFromPacket(decoded));
      if (duplicate.duplicate) this.#counters[channel].duplicate += 1;
    }
  }

  async #verifyGeneration(generation: MutableGeneration): Promise<void> {
    const reconstructed = generation.reconstructed;
    const expected = this.#sealed.chunks[generation.encoder.generationId];
    if (reconstructed === null || expected === undefined) {
      throw new IntegrityError(
        'chunk-hashes',
        `Generation ${generation.encoder.generationId} failed encrypted-chunk verification.`,
      );
    }
    const authenticatedPlaintext = await openSealedChunk(
      this.#sealed,
      generation.encoder.generationId,
      reconstructed,
      { discoveryCommitment: this.#discoveryCommitment },
    );
    generation.verified = true;
    this.#reconstructedChunks[generation.encoder.generationId] = reconstructed;
    this.#verifiedBytes += authenticatedPlaintext.length;
    const totalContributions =
      generation.opticalContributions + generation.acousticContributions;
    if (totalContributions > 0) {
      this.#verifiedByChannel.optical +=
        expected.plaintextLength * (generation.opticalContributions / totalContributions);
      this.#verifiedByChannel.acoustic +=
        expected.plaintextLength * (generation.acousticContributions / totalContributions);
    }
  }

  async #verify(): Promise<void> {
    if (this.#verificationResolved === null) {
      const chunks = this.#reconstructedChunks.map((chunk, index) => {
        if (chunk === null) {
          throw new IntegrityError('chunk-hashes', `Generation ${index} is unresolved.`);
        }
        return chunk;
      });
      this.#verificationResolved = await openSealedObject(this.#sealed, chunks, {
        discoveryCommitment: this.#discoveryCommitment,
      });
      this.#receiverObjectHash = await hashHex(this.#verificationResolved);
    }
    if (this.#phaseElapsedMs >= (this.#verificationStep + 1) * 240) {
      this.#verificationStep += 1;
    }
    if (this.#verificationStep >= 6) {
      this.#downloadBytes = this.#verificationResolved;
      this.#transition('COMPLETE');
    }
  }

  #nextIncompleteGeneration(): MutableGeneration | null {
    if (this.#profile.directedRepair) {
      let target: MutableGeneration | null = null;
      let targetDeficit = Number.POSITIVE_INFINITY;
      for (const generation of this.#generations) {
        if (generation.decoder.complete) continue;
        const deficit = generation.encoder.sourceCount - generation.decoder.rank;
        if (
          deficit < targetDeficit ||
          (deficit === targetDeficit &&
            (target === null ||
              generation.encoder.generationId < target.encoder.generationId))
        ) {
          target = generation;
          targetDeficit = deficit;
        }
      }
      return target;
    }

    const count = this.#generations.length;
    for (let offset = 0; offset < count; offset += 1) {
      const index = (this.#generationCursor + offset) % count;
      const generation = this.#generations[index];
      if (generation !== undefined && !generation.decoder.complete) {
        this.#generationCursor = (index + 1) % count;
        return generation;
      }
    }
    return null;
  }

  #channelFailure(
    channel: PhysicalChannel,
    displayObstruction: boolean,
    audioInterference: boolean,
  ): boolean {
    const probability =
      channel === 'optical'
        ? displayObstruction
          ? 0.52
          : 0.035
        : audioInterference
          ? 0.69
          : 0.085;
    return this.#randomUnit() < probability;
  }

  #randomUnit(): number {
    this.#randomState = xorshift(this.#randomState);
    return this.#randomState / 0x1_0000_0000;
  }

  #packetFromSymbol(symbol: FountainSymbol, channel: PhysicalChannel): TransportPacket {
    this.#sequence += 1;
    return {
      sessionTag: this.#sessionTag,
      objectTag: this.#objectTag,
      direction: 'sender-to-receiver',
      channel,
      sequence: this.#sequence,
      generationId: symbol.generationId,
      equationId: symbol.equationId,
      sourceCount: symbol.sourceCount,
      symbolSize: symbol.symbolSize,
      validGenerationBytes: symbol.validBytes,
      payload: symbol.payload,
    };
  }

  #symbolFromPacket(packet: TransportPacket): FountainSymbol {
    return {
      generationId: packet.generationId,
      equationId: packet.equationId,
      sourceCount: packet.sourceCount,
      symbolSize: packet.symbolSize,
      validBytes: packet.validGenerationBytes,
      payload: packet.payload,
    };
  }

  #profileForChannel(channel: PhysicalChannel): string {
    return channel === 'optical'
      ? this.#profile.opticalProfile
      : `A1-OFDM-${this.#profile.acousticCeiling}`;
  }

  #generationTelemetry(): readonly GenerationTelemetry[] {
    return this.#generations.map((generation) => ({
      generationId: generation.encoder.generationId,
      rank: generation.decoder.rank,
      requiredRank: generation.encoder.sourceCount,
      opticalContributions: generation.opticalContributions,
      acousticContributions: generation.acousticContributions,
      verified: generation.verified,
    }));
  }

  #integrityChecks(): readonly IntegrityCheck[] {
    const stages = [
      [
        'physical-crc',
        'Modelled PHY admission',
        'Deterministic erasures and SBFA authentication gate admissions; O1/A1 CRC paths are tested separately',
      ],
      ['fountain-rank', 'Fountain reconstruction', 'Only independent equations increase rank'],
      ['chunk-hashes', 'Per-chunk SHA-256', 'Every reconstructed ciphertext chunk is committed'],
      ['merkle-root', 'Merkle root', 'The complete ordered chunk set matches the manifest'],
      ['aead-tags', 'AES-256-GCM tags', 'Authenticated decryption binds object metadata'],
      ['object-hash', 'Final object SHA-256', 'Receiver bytes match the sender object'],
    ] as const;
    const rankComplete = this.#generations.every((generation) => generation.decoder.complete);
    const chunksComplete = this.#generations.every((generation) => generation.verified);
    return stages.map(([stage, label, detail], index) => {
      let state: IntegrityCheck['state'] = 'pending';
      if (this.#phase === 'FAILED' && this.#integrityViolations > 0) {
        state = 'failed';
      } else if (stage === 'physical-crc') {
        state = this.#counters.optical.accepted + this.#counters.acoustic.accepted > 0 ? 'verified' : 'active';
      } else if (stage === 'fountain-rank') {
        state = rankComplete ? 'verified' : this.#phase === 'TRANSFER' || this.#phase === 'REPAIR' ? 'active' : 'pending';
      } else if (stage === 'chunk-hashes') {
        state = chunksComplete ? 'verified' : this.#verifiedBytes > 0 ? 'active' : 'pending';
      } else if (stage === 'aead-tags') {
        state = chunksComplete ? 'verified' : this.#verifiedBytes > 0 ? 'active' : 'pending';
      } else if (this.#phase === 'COMPLETE' || this.#verificationStep > index - 1) {
        state = 'verified';
      } else if (this.#phase === 'VERIFY' && this.#verificationStep === index - 2) {
        state = 'active';
      }
      return { stage, label, state, detail };
    });
  }

  #transition(to: TransferPhase): void {
    if (to === 'TRANSFER' && !this.#transferAuthorised()) {
      throw new ProtocolError(
        'KEY_CONFIRMATION_REQUIRED',
        'Transfer cannot begin before every active channel is mutually key-confirmed.',
      );
    }
    assertPhaseTransition(this.#phase, to);
    this.#phase = to;
    this.#phaseElapsedMs = 0;
  }

  #isTerminal(): boolean {
    return this.#phase === 'COMPLETE' || this.#phase === 'FAILED';
  }
}

function sanitiseFilename(name: string): string {
  const stripped = name
    .normalize('NFC')
    .replace(/[\\/:*?"<>|\p{Cf}\u0000-\u001f\u007f-\u009f]/gu, '_')
    .trim()
    .replace(/[. ]+$/gu, '');
  const bounded = stripped.slice(0, 180).replace(/[. ]+$/gu, '');
  return bounded || 'received-object.bin';
}

const MIME_TOKEN = "[!#$%&'*+.^_`|~\\dA-Za-z-]+";
const MIME_TYPE_PATTERN = new RegExp(`^${MIME_TOKEN}/${MIME_TOKEN}$`, 'u');

function sanitiseMimeType(value: string): string {
  const candidate = value.trim();
  if (
    candidate.length > 255 ||
    candidate.includes('*') ||
    !MIME_TYPE_PATTERN.test(candidate)
  ) {
    return 'application/octet-stream';
  }
  return candidate.toLowerCase();
}

function corruptWire(wire: Uint8Array, seed: number): Uint8Array {
  const corrupted = wire.slice();
  const index = 50 + (seed % Math.max(1, wire.length - 54));
  corrupted[index] = (corrupted[index] ?? 0) ^ 0x40;
  return corrupted;
}

function incidentState(progress: number): LinkSnapshot['incidents'] {
  return [
    {
      id: 'display-obstruction',
      label: 'Camera obstruction',
      active: progress >= 0.22 && progress < 0.39,
      resolved: progress >= 0.39,
    },
    {
      id: 'audio-interference',
      label: 'Acoustic interference',
      active: progress >= 0.43 && progress < 0.59,
      resolved: progress >= 0.59,
    },
  ];
}

function acousticSnr(interference: boolean): readonly number[] {
  return Array.from({ length: 48 }, (_, index) => {
    const baseline = 20 + 8 * Math.sin(index * 0.61) + 4 * Math.cos(index * 0.17);
    const notch = interference && index >= 17 && index <= 31 ? 18 : 0;
    return Math.max(-2, baseline - notch);
  });
}

function cappedModulation(
  modulation: AudioModulation,
  ceiling: ControllerProfile['acousticCeiling'],
): AudioModulation {
  if (modulation === 'OFF' || modulation === 'PILOT') return modulation;
  if (ceiling === 'BPSK') return 'BPSK';
  if (ceiling === 'QPSK' && modulation === '16QAM') return 'QPSK';
  return modulation;
}

function modulationBits(modulation: AudioModulation): number {
  switch (modulation) {
    case 'BPSK':
      return 1;
    case 'QPSK':
      return 2;
    case '16QAM':
      return 4;
    case 'OFF':
    case 'PILOT':
      return 0;
  }
}

function createSimulatedAcousticPlan(
  interference: boolean,
  ceiling: ControllerProfile['acousticCeiling'],
): AudioCarrierPlan {
  const allocated = allocateCarriers(acousticSnr(interference), 48_000, 'simulated');
  const carriers = allocated.carriers.map((carrier) => {
    const modulation = cappedModulation(carrier.modulation, ceiling);
    return {
      ...carrier,
      modulation,
      bitsPerSymbol: modulationBits(modulation),
      enabled: modulation !== 'OFF',
    };
  });
  return {
    ...allocated,
    carriers,
    usableCarrierCount: carriers.filter((carrier) => carrier.enabled).length,
  };
}

function createCarriers(
  interference: boolean,
  ceiling: ControllerProfile['acousticCeiling'],
): readonly CarrierTelemetry[] {
  return createSimulatedAcousticPlan(interference, ceiling).carriers.map((carrier) => ({
    index: carrier.index,
    frequencyHz: carrier.frequencyHz,
    snrDb: carrier.snrDb,
    modulation: carrier.modulation,
    enabled: carrier.enabled,
  }));
}

function estimateOpticalAirtime(
  payloadBytes: number,
  profile: ControllerProfile,
): {
  readonly payloadBytesPerRaster: number;
  readonly rasterFrames: number;
  readonly durationSeconds: number;
} {
  if (!Number.isSafeInteger(payloadBytes) || payloadBytes < 1) {
    throw new ProtocolError(
      'DEMO_AIRTIME_BOUNDS',
      'Transport airtime requires a positive bounded payload length.',
    );
  }
  if (
    !Number.isFinite(profile.opticalCellScale) ||
    profile.opticalCellScale <= 0 ||
    !Number.isSafeInteger(profile.opticalDwellFrames) ||
    profile.opticalDwellFrames < 1
  ) {
    throw new ProtocolError(
      'DEMO_PROFILE_BOUNDS',
      'Optical profile timing and cell scale must be finite and positive.',
    );
  }
  const payloadBytesPerRaster = Math.max(
    1,
    Math.min(
      OPTICAL_PHYSICAL_PAYLOAD_BYTES,
      Math.floor(
        OPTICAL_PHYSICAL_PAYLOAD_BYTES /
          (profile.opticalCellScale * profile.opticalCellScale),
      ),
    ),
  );
  const rasterFrames = Math.ceil(payloadBytes / payloadBytesPerRaster);
  return {
    payloadBytesPerRaster,
    rasterFrames,
    durationSeconds:
      (rasterFrames * profile.opticalDwellFrames) / OPTICAL_REFRESH_HZ,
  };
}

export function estimateDemoTransportAirtime(
  payloadBytes: number,
  profile: ControllerProfile,
  audioInterference = false,
): DemoTransportAirtimeEstimate {
  const optical = estimateOpticalAirtime(payloadBytes, profile);
  const acoustic = estimateOfdmTransmission(
    payloadBytes,
    createSimulatedAcousticPlan(audioInterference, profile.acousticCeiling),
  );
  const opticalAttemptsPerSecond =
    profile.opticalAllocation / optical.durationSeconds;
  const acousticAttemptsPerSecond =
    profile.acousticAllocation / acoustic.durationSeconds;
  return {
    payloadBytes,
    opticalPayloadBytesPerRaster: optical.payloadBytesPerRaster,
    opticalRasterFrames: optical.rasterFrames,
    opticalDurationSeconds: optical.durationSeconds,
    acousticBitsPerSymbol: acoustic.bitsPerSymbol,
    acousticDurationSeconds: acoustic.durationSeconds,
    aggregatePayloadBitsPerSecond:
      payloadBytes * 8 * (opticalAttemptsPerSecond + acousticAttemptsPerSecond),
  };
}

function createSpatialRegions(obstructed: boolean): readonly SpatialRegionTelemetry[] {
  return Array.from({ length: 16 }, (_, index) => {
    const affected = obstructed && (index === 5 || index === 6 || index === 9 || index === 10);
    return {
      index,
      quality: affected ? 0.18 : 0.88 + ((index * 17) % 11) / 100,
      enabled: !affected,
    };
  });
}
